/**
 * Framework7 pages often use CSS transforms, which make position:fixed
 * relative to that ancestor while getBoundingClientRect stays viewport-based.
 * Append to document.body and use viewport coords to avoid the mismatch.
 *
 * Hover UX: wait 300ms with the pointer still, then show once under the cursor
 * and keep that position until the pointer leaves the target.
 */
const DEFAULT_SHOW_DELAY_MS = 300;
const CURSOR_GAP_PX = 12;

function clampTooltipPosition(left, top, width, height) {
  let nextLeft = left;
  let nextTop = top;

  if (nextTop + height > window.innerHeight - 8) {
    nextTop = Math.max(8, top - height - CURSOR_GAP_PX * 2);
  }
  if (nextLeft < 8) {
    nextLeft = 8;
  }
  if (nextLeft + width > window.innerWidth - 8) {
    nextLeft = window.innerWidth - width - 8;
  }
  if (nextTop < 8) {
    nextTop = 8;
  }

  return { left: nextLeft, top: nextTop };
}

const TOOLTIP_ERR_CLASS = 'err';

export function createFixedF7Tooltip(
  f7,
  {
    targetEl,
    text,
    trigger,
    showDelay = DEFAULT_SHOW_DELAY_MS,
    cssClass,
    clickable = false
  } = {}
) {
  if (!f7 || !targetEl || !text) {
    return null;
  }

  const tooltip = f7.tooltip.create({
    targetEl,
    text,
    trigger
  });

  if (!tooltip || !tooltip.$el) {
    return tooltip;
  }

  // Take over hover: F7 shows immediately; we need a dwell delay instead.
  if (typeof tooltip.detachEvents === 'function') {
    tooltip.detachEvents();
  }

  tooltip.$el.css({
    position: 'fixed',
    zIndex: 20000,
    pointerEvents: 'none'
  });

  const applyCssClass = nextClass => {
    tooltip.$el.removeClass(TOOLTIP_ERR_CLASS);
    if (nextClass) {
      tooltip.$el.addClass(nextClass);
    }
  };
  applyCssClass(cssClass);
  tooltip.setCssClass = applyCssClass;

  const originalShow = tooltip.show.bind(tooltip);
  const originalHide = tooltip.hide.bind(tooltip);
  const originalDestroy = tooltip.destroy.bind(tooltip);
  const anchor = { x: 0, y: 0 };
  let dwellTimer = null;
  let dwellPoint = null;
  // Touch/mobile: click only (hover dwell fights with tap). Desktop: hover + click.
  const hoverEnabled = !clickable || Boolean(window.isDesktop);

  const ensureInBody = () => {
    if (tooltip.$el[0] && tooltip.$el[0].parentNode !== document.body) {
      document.body.appendChild(tooltip.$el[0]);
    }
  };

  const clearDwellTimer = () => {
    if (dwellTimer) {
      clearTimeout(dwellTimer);
      dwellTimer = null;
    }
  };

  const applyFixedCss = (left, top) => {
    tooltip.$el.css({
      top: `${top}px`,
      left: `${left}px`,
      position: 'fixed',
      zIndex: 20000,
      pointerEvents: 'none'
    });
  };

  const setAnchorFromTarget = () => {
    const rect = targetEl.getBoundingClientRect();
    anchor.x = rect.left + rect.width / 2;
    anchor.y = rect.bottom;
  };

  tooltip.position = function positionAtAnchor() {
    ensureInBody();

    const width = this.$el.outerWidth();
    const height = this.$el.outerHeight();
    const { left, top } = clampTooltipPosition(
      anchor.x + CURSOR_GAP_PX,
      anchor.y + CURSOR_GAP_PX,
      width,
      height
    );
    applyFixedCss(left, top);
  };

  tooltip.show = function showFixed(aroundEl) {
    ensureInBody();
    const result = originalShow(aroundEl);
    this.position();
    return result;
  };

  tooltip.hide = function hideFixed() {
    clearDwellTimer();
    dwellPoint = null;
    return originalHide();
  };

  const showAtDwellPoint = () => {
    dwellTimer = null;
    if (!dwellPoint) {
      return;
    }
    anchor.x = dwellPoint.x;
    anchor.y = dwellPoint.y;
    tooltip.show(targetEl);
  };

  const scheduleShow = (clientX, clientY) => {
    if (tooltip.opened) {
      // Already visible at a fixed spot — do not chase the cursor.
      return;
    }

    if (dwellPoint && dwellPoint.x === clientX && dwellPoint.y === clientY) {
      return;
    }

    dwellPoint = { x: clientX, y: clientY };
    clearDwellTimer();
    dwellTimer = setTimeout(showAtDwellPoint, showDelay);
  };

  const onPointerEnter = event => {
    scheduleShow(event.clientX, event.clientY);
  };

  const onPointerMove = event => {
    scheduleShow(event.clientX, event.clientY);
  };

  const onPointerLeave = () => {
    clearDwellTimer();
    dwellPoint = null;
    if (tooltip.opened) {
      originalHide();
    }
  };

  const onClick = event => {
    event.preventDefault();
    event.stopPropagation();
    clearDwellTimer();
    dwellPoint = null;
    if (tooltip.opened) {
      originalHide();
      return;
    }
    setAnchorFromTarget();
    tooltip.show(targetEl);
  };

  if (hoverEnabled) {
    targetEl.addEventListener('pointerenter', onPointerEnter);
    targetEl.addEventListener('pointermove', onPointerMove);
    targetEl.addEventListener('pointerleave', onPointerLeave);
  }
  if (clickable) {
    targetEl.addEventListener('click', onClick);
  }

  tooltip.destroy = function destroyFixed() {
    clearDwellTimer();
    dwellPoint = null;
    if (hoverEnabled) {
      targetEl.removeEventListener('pointerenter', onPointerEnter);
      targetEl.removeEventListener('pointermove', onPointerMove);
      targetEl.removeEventListener('pointerleave', onPointerLeave);
    }
    if (clickable) {
      targetEl.removeEventListener('click', onClick);
    }
    return originalDestroy();
  };

  return tooltip;
}
